// Custom filter functions
const dateRangeFilterFn = (
  row: any,
  columnId: string,
  filterValue: { startDate: string; endDate: string }
) => {
  if (!filterValue || !filterValue.startDate || !filterValue.endDate) {
    return true;
  }

  const cellValue = row.getValue(columnId);
  if (!cellValue) {
    return false;
  }

  // Convert cell value to date string in YYYY-MM-DD format
  const cellDate = new Date(cellValue);
  const cellDateStr = cellDate.toISOString().split('T')[0];

  return cellDateStr >= filterValue.startDate && cellDateStr <= filterValue.endDate;
};

// boolean filter
const booleanFilterFn = (row: any, columnId: string, filterValue: boolean) => {
  if (!filterValue) return true;
  const cellValue = row.getValue(columnId) as boolean;
  return cellValue?.toString() === filterValue?.toString();
};

// number range filter
const inNumberRangeFilterFn = (
  row: any,
  columnId: string,
  filterValue: { start?: number; end?: number }
) => {
  if (!filterValue || (filterValue.start === undefined && filterValue.end === undefined)) {
    return true;
  }

  const cellValue = row.getValue(columnId);
  if (cellValue === null || cellValue === undefined) {
    return false;
  }

  const numericValue = typeof cellValue === 'string' ? parseFloat(cellValue) : cellValue;

  if (isNaN(numericValue)) {
    return false;
  }

  const start = filterValue.start !== undefined ? filterValue.start : -Infinity;
  const end = filterValue.end !== undefined ? filterValue.end : Infinity;

  return numericValue >= start && numericValue <= end;
};

const enumFilterFn = (row: any, columnId: string, filterValue: string[]) => {
  if (!filterValue || filterValue.length === 0) {
    return true;
  }

  const cellValue = row.getValue(columnId);
  if (cellValue === null || cellValue === undefined) {
    return false;
  }

  const cellValueStr = String(cellValue);

  return filterValue.includes(cellValueStr);
};

// Custom sorting function for money fields to ensure numeric sorting
const moneySortingFn = (rowA: any, rowB: any, columnId: string) => {
  const parseMoney = (value: any) => {
    if (value === null || value === undefined) {
      return 0;
    }

    // If it's already a number, return it
    if (typeof value === 'number') {
      return value;
    }

    // If it's a string, remove currency symbols and parse as float
    const numericValue = value.toString().replace(/[^0-9.-]+/g, '');
    const parsed = parseFloat(numericValue);
    return isNaN(parsed) ? 0 : parsed;
  };

  const valueA = parseMoney(rowA.getValue(columnId));
  const valueB = parseMoney(rowB.getValue(columnId));

  return valueA > valueB ? 1 : valueA < valueB ? -1 : 0;
};

// Custom filter function for IBAN fields that handles both formatted and unformatted input
const ibanFilterFn = (row: any, columnId: string, filterValue: string) => {
  if (!filterValue || filterValue.trim() === '') {
    return true;
  }

  const cellValue = row.getValue(columnId);
  if (!cellValue) {
    return false;
  }

  // Normalize both values by removing spaces and converting to uppercase
  const normalizeIban = (iban: string) => {
    return iban.toString().replace(/\s+/g, '').toUpperCase();
  };

  const normalizedCellValue = normalizeIban(cellValue);
  const normalizedFilterValue = normalizeIban(filterValue);

  // Check if the normalized cell value includes the normalized filter value
  return normalizedCellValue.includes(normalizedFilterValue);
};

// Custom filter function for arrays of objects with order_number property
const ordersArrayFilterFn = (row: any, columnId: string, filterValue: string) => {
  if (!filterValue || filterValue.trim() === '') {
    return true;
  }

  const orders = row.getValue(columnId);
  if (!orders || !Array.isArray(orders)) {
    return false;
  }

  // Search through all order_number properties in the orders array
  const searchTerm = filterValue.toLowerCase().trim();
  return orders.some((order: any) => {
    if (!order || !order.order_number) {
      return false;
    }
    return order.order_number.toString().toLowerCase().includes(searchTerm);
  });
};

// Generic filter function for flat arrays with partial matching
const arrayIncludesPartialFilterFn = (row: any, columnId: string, filterValue: string) => {
  if (!filterValue || filterValue.trim() === '') {
    return true;
  }

  const arrayValue = row.getValue(columnId);
  if (!arrayValue || !Array.isArray(arrayValue)) {
    return false;
  }

  // Search through all items in the array for partial matches
  const searchTerm = filterValue.toLowerCase().trim();
  return arrayValue.some((item: any) => {
    if (item === null || item === undefined) {
      return false;
    }
    return item.toString().toLowerCase().includes(searchTerm);
  });
};

export {
  arrayIncludesPartialFilterFn,
  booleanFilterFn,
  dateRangeFilterFn,
  enumFilterFn,
  ibanFilterFn,
  inNumberRangeFilterFn,
  moneySortingFn,
  ordersArrayFilterFn,
};
